import { makeStyles } from '@material-ui/core/styles'
import { calcMargin } from '@v4/utils/theme'
import { isDoubleGrid, isPositionContained } from '@v4/components/sections/Question/Question.utils'
import { MEDIA_POSITION } from './Question.const'

const useStyles = makeStyles(
	theme => ({
		root: {
			flex: 1,
			maxWidth: '100%',

			flexDirection: ({ isMobile, position }) => {
				if (!isMobile) return null

				return position === MEDIA_POSITION.TOP_COVER ? 'column' : null
			},
		},

		wrapper: {
			width: '100%',
			height: ({ position, isAssetImage }) => {
				return isPositionContained(position) && isAssetImage ? '100%' : null
			},
		},

		content: {
			display: 'flex',
			zIndex: 10,
			height: '100%',
			gap: ({ position, isMobile, showMedia }) => {
				if (isMobile || !showMedia) return null

				return isDoubleGrid(position) ? theme.spacing(7) : null
			},
		},

		background: {
			position: 'absolute',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			maxWidth: '100%',
			overflow: 'hidden',
			backgroundPosition: 'center',
			backgroundSize: 'cover',
			backgroundRepeat: 'no-repeat',
			zIndex: 1,

			'&:after': {
				content: '""',
				position: 'absolute',
				backgroundColor: 'black',
				width: '100%',
				height: '100%',
				top: 0,
				left: 0,
				opacity: ({ shading }) => (shading || 0) / 100,
			},

			'& > div > div': {
				pointerEvents: 'all',
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				zIndex: ({ isAssetImage }) => (isAssetImage ? null : 11),
			},
		},

		overlayOpacity: ({ opacity }) => ({
			'&:after': {
				opacity: (opacity?.toNumber?.() || 0) / 100,
			},
		}),

		topCoverWrapper: {
			position: 'relative',
			width: '100%',
			aspectRatio: '16/9',
			marginBottom: theme.spacing(2),
			marginTop: ({ headerMarginTop, headerPosition, minMarginTop, isMobile, marginTop }) => {
				if (!isMobile) return null

				return calcMargin({
					spacing: theme.spacing,
					value: headerPosition ? headerMarginTop?.meta_value : marginTop?.metaValue,
					minMargin: minMarginTop,
					isMobile: !headerPosition,
					negative: true,
				})
			},
			zIndex: 2,
		},

		gridItem: {
			flexBasis: ({ position, isMobile, showMedia }) => {
				if (isMobile || !showMedia) return null

				return isDoubleGrid(position) ? `calc(50% - ${theme.spacing(3.5)})` : null
			},
			maxWidth: ({ position, isMobile, showMedia }) => {
				if (isMobile || !showMedia) return null

				return isDoubleGrid(position) ? `calc(50% - ${theme.spacing(3.5)})` : null
			},
		},

		gridContained: {
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
		},

		lock: {
			justifyContent: 'center',
			textAlign: 'center',
			width: '100%',
			position: 'relative',
			zIndex: 20,
			cursor: 'pointer',
			'& a': {
				color: 'inherit',
				textDecoration: 'underline',
				'&:hover, &:focus': {
					color: 'inherit',
					textDecoration: 'none',
				},
			},
		},

		desktopImage: {
			transition: theme.transitions.create(['margin-top', 'margin-bottom']),
			[theme.breakpoints.up('md')]: {
				marginTop: ({ marginTop }) =>
					marginTop?.toNumber() > 0 ? theme.spacing(-marginTop?.toNumber() / 10) : null,
				marginBottom: ({ marginBottom }) =>
					marginBottom?.toNumber() > 0 ? theme.spacing(-marginBottom?.toNumber() / 10) : null,
			},
		},

		cover: {
			position: 'absolute',
			top: 0,
			left: ({ position }) => (position === MEDIA_POSITION.RIGHT_COVER ? '50%' : 0),
			right: ({ position }) => (position === MEDIA_POSITION.LEFT_COVER ? '50%' : 0),
			width: '50%',
			bottom: 0,
			overflow: 'hidden',
			backgroundPosition: 'center',
			backgroundSize: 'cover',
			backgroundRepeat: 'no-repeat',
			zIndex: ({ isAssetImage }) => (isAssetImage ? 1 : null),

			'&:after': {
				content: '""',
				position: 'absolute',
				backgroundColor: 'black',
				width: '100%',
				height: '100%',
				top: 0,
				left: 0,
				opacity: ({ shading }) => (shading || 0) / 100,
			},

			'& > div > div': {
				pointerEvents: 'all',
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				zIndex: ({ isAssetImage }) => (isAssetImage ? null : 11),
			},
		},

		contain: {
			width: '100%',
			height: '100%',

			'& > div > div': {
				backgroundSize: 'contain',
				backgroundPosition: 'center',
			},
		},

		aboveAndBelowWrapper: {
			display: 'flex',
			justifyContent: ({ alignFlex }) => alignFlex,
			marginBottom: theme.spacing(2),
		},

		aboveAndBelow: {
			width: '100%',
			height: ({ isAssetImage }) => (isAssetImage ? theme.spacing(21.5) : '100%'),
			maxWidth: theme.spacing(62),
			aspectRatio: ({ isAssetImage }) => (isAssetImage ? null : '16/9'),
		},
	}),
	{ name: 'Question' },
)

export default useStyles
